import { type ReactNode, useCallback, useEffect, useState, Fragment } from 'react';
import { flexRender, useTable } from '@tanstack/react-table';
import {
    dataTableFeatures,
    type ColumnDef,
    type SortingState,
    type ExpandedState,
    type Row,
    type RowData,
    type Updater
} from './dataTableConfig';
import FormInput from './FormInput';
import { FaChevronRight, FaChevronDown } from 'react-icons/fa';

export interface ExpandableDataTableProps<TData extends RowData> {
    data: TData[];
    columns: ColumnDef<TData>[];
    search?: boolean;
    sort?: boolean;
    pagination?: { enabled: boolean; pageSize: number };
    striped?: boolean;
    allowOverflow?: boolean;
    onRowClick?: (row: TData) => void;
    renderSubComponent?: (props: { row: Row<TData> }) => ReactNode;
    getRowCanExpand?: (row: Row<TData>) => boolean;
    expandAllByDefault?: boolean;
    initialSorting?: SortingState;
    /** Controlled search term. Omit to let the table own it. */
    globalFilter?: string;
    onGlobalFilterChange?: (value: string) => void;
    /** Fired when the user presses Enter in the search box, e.g. to jump straight to an exact match. */
    onSearchSubmit?: (value: string) => void;
    /** Replaces the default search syntax tips, e.g. to advertise a shortcut for the current term. */
    searchHint?: ReactNode;
    /** Stable row identity; keeps expansion state attached to the right row when `data` changes. */
    getRowId?: (row: TData) => string;
}

function universalSort<TData extends RowData>(rowA: Row<TData>, rowB: Row<TData>, columnId: string): number {
    const parseValue = (val: unknown) => {
        if (typeof val === 'number') return { type: 'number', value: val };

        if (typeof val === 'string') {
            const dateTimeRegex = /^\d{4}-\d{2}-\d{2}(\s\d{2}:\d{2}(:\d{2})?)?$/;

            if (dateTimeRegex.test(val)) {
                const date = new Date(val);

                if (!isNaN(date.getTime())) {
                    return { type: 'date', value: date.getTime() };
                }
            }

            const num = parseFloat(val.replace(/[^0-9.-]+/g, ''));
            if (!isNaN(num)) return { type: 'number', value: num };
        }

        return null;
    };

    const aRaw = rowA.getValue(columnId);
    const bRaw = rowB.getValue(columnId);

    const aParsed = parseValue(aRaw);
    const bParsed = parseValue(bRaw);

    if (aParsed !== null && bParsed !== null) {
        return aParsed.value - bParsed.value;
    }

    return String(aRaw).localeCompare(String(bRaw));
}

export function ExpandableDataTable<TData extends RowData>({
    data,
    columns,
    search = true,
    sort = true,
    pagination = { enabled: false, pageSize: 20 },
    striped = true,
    allowOverflow = true,
    onRowClick,
    renderSubComponent,
    getRowCanExpand,
    expandAllByDefault = false,
    initialSorting = [],
    globalFilter: controlledGlobalFilter,
    onGlobalFilterChange,
    onSearchSubmit,
    searchHint,
    getRowId
}: ExpandableDataTableProps<TData>) {
    const [internalGlobalFilter, setInternalGlobalFilter] = useState('');
    const [sorting, setSorting] = useState<SortingState>(initialSorting);
    const [expanded, setExpanded] = useState<ExpandedState>(expandAllByDefault ? true : {});

    const isFilterControlled = controlledGlobalFilter !== undefined;
    const globalFilter = isFilterControlled ? controlledGlobalFilter : internalGlobalFilter;

    const setGlobalFilter = useCallback(
        (next: string) => {
            if (!isFilterControlled) {
                setInternalGlobalFilter(next);
            }

            onGlobalFilterChange?.(next);
        },
        [isFilterControlled, onGlobalFilterChange]
    );

    const handleTableGlobalFilterChange = useCallback(
        (updater: Updater<string>) => {
            setGlobalFilter(typeof updater === 'function' ? updater(globalFilter) : updater);
        },
        [globalFilter, setGlobalFilter]
    );

    const [paginationState, setPaginationState] = useState({
        pageIndex: 0,
        pageSize: pagination?.enabled ? pagination.pageSize : 20
    });

    // A changed search term must start from the first page, otherwise a stale page index shows an empty table.
    useEffect(() => {
        setPaginationState((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
    }, [globalFilter]);

    // Add expansion column if renderSubComponent is provided
    const tableColumns = renderSubComponent
        ? [
              {
                  id: 'expander',
                  header: () => null,
                  cell: ({ row }: { row: Row<TData> }) => {
                      return row.getCanExpand() ? (
                          <button
                              aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
                              className="p-1 hover:bg-zinc-700 rounded transition-colors"
                              onClick={(e) => {
                                  e.stopPropagation();
                                  row.toggleExpanded();
                              }}
                          >
                              {row.getIsExpanded() ? (
                                  <FaChevronDown className="text-zinc-400" />
                              ) : (
                                  <FaChevronRight className="text-zinc-400" />
                              )}
                          </button>
                      ) : null;
                  },
                  size: 40,
                  minSize: 40,
                  maxSize: 40
              },
              ...columns
          ]
        : columns;

    const table = useTable({
        features: dataTableFeatures,
        data,
        columns: tableColumns,
        enableSorting: sort,
        manualSorting: !sort,
        manualPagination: !pagination.enabled,
        state: {
            ...(search ? { globalFilter } : {}),
            ...(sort ? { sorting } : {}),
            ...(pagination?.enabled ? { pagination: paginationState } : {}),
            expanded
        },
        onGlobalFilterChange: search ? handleTableGlobalFilterChange : undefined,
        onSortingChange: sort ? setSorting : undefined,
        onPaginationChange: pagination?.enabled ? setPaginationState : undefined,
        onExpandedChange: setExpanded,
        getRowCanExpand: getRowCanExpand || (() => !!renderSubComponent),
        ...(getRowId ? { getRowId } : {}),
        autoResetPageIndex: false,
        defaultColumn: {
            sortFn: universalSort,
            minSize: 50,
            size: 150,
            maxSize: Number.MAX_SAFE_INTEGER
        },
        columnResizeMode: 'onChange',
        // Case-insensitive multi-term search with AND/OR/NOT
        // Syntax: space = AND, 'a|b' = OR, '-token' = NOT
        globalFilterFn: (row, _columnId, value) => {
            const raw = String(value).trim().toLowerCase();
            if (!raw) return true;

            const haystack = row
                .getAllCells()
                .map((cell) => (cell.getValue() != null ? String(cell.getValue()).toLowerCase() : ''))
                .join(' ');

            const tokens = raw.split(/\s+/);

            for (const tok of tokens) {
                if (!tok) continue;
                const isNeg = tok.startsWith('-');
                const body = isNeg ? tok.slice(1) : tok;
                const alts = body.split('|').filter(Boolean);

                if (alts.length === 0) continue;

                if (isNeg) {
                    const anyPresent = alts.some((alt) => haystack.includes(alt));
                    if (anyPresent) return false;
                } else {
                    const anyPresent = alts.some((alt) => haystack.includes(alt));
                    if (!anyPresent) return false;
                }
            }

            return true;
        }
    });

    const handleExpandAll = () => {
        table.toggleAllRowsExpanded();
    };

    const allExpanded = table.getIsAllRowsExpanded();

    return (
        <div className="w-full max-w-full">
            <div className="flex items-center justify-between mb-2 sm:mb-3 md:mb-4">
                {search && (
                    <FormInput
                        className="w-full md:w-64"
                        placeholder="Search..."
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                onSearchSubmit?.(globalFilter);
                            }
                        }}
                    />
                )}
                {search && (
                    <div className="ml-3 text-xs text-zinc-500">
                        {searchHint ?? (
                            <>
                                Tips: space = AND, use &quot;|&quot; for OR, prefix with &quot;-&quot; to exclude. Example: spy candle|-wick
                                -futures
                            </>
                        )}
                    </div>
                )}
                {renderSubComponent && (
                    <button
                        className="ml-auto px-3 py-1 text-sm bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded transition-colors"
                        onClick={handleExpandAll}
                    >
                        {allExpanded ? 'Collapse All' : 'Expand All'}
                    </button>
                )}
            </div>

            <div className={`${allowOverflow ? 'overflow-x-auto' : 'overflow-hidden'}`}>
                <table
                    className={`w-full divide-y divide-zinc-800 ${striped ? 'bg-[#222222] [&>tbody>*:nth-child(odd)]:bg-[#2a2a2a]' : ''}`}
                >
                    <thead className="bg-[#131416]">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={`header-group-${headerGroup.id}`}>
                                {headerGroup.headers.map((header) => {
                                    const canSort = header.column.getCanSort();
                                    return (
                                        <th
                                            className={`px-2 py-1 sm:px-3 sm:py-2 md:px-3 md:py-3 text-left text-xs font-medium text-zinc-300 uppercase tracking-wider overflow-hidden ${
                                                canSort ? 'cursor-pointer hover:bg-[#252525]' : ''
                                            }`}
                                            key={`header-${header.id}`}
                                            style={{
                                                minWidth: header.column.columnDef.minSize
                                                    ? `${header.column.columnDef.minSize}px`
                                                    : undefined,
                                                maxWidth: header.column.columnDef.maxSize
                                                    ? `${header.column.columnDef.maxSize}px`
                                                    : undefined,
                                                width: header.column.columnDef.size ? `${header.column.columnDef.size}px` : undefined
                                            }}
                                            onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                                        >
                                            <div className="flex items-center">
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {canSort && (
                                                    <span className="ml-1">
                                                        {header.column.getIsSorted() === 'asc' ? (
                                                            <svg
                                                                className="h-4 w-4 inline"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    d="M5 15l7-7 7 7"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth="2"
                                                                />
                                                            </svg>
                                                        ) : header.column.getIsSorted() === 'desc' ? (
                                                            <svg
                                                                className="h-4 w-4 inline"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    d="M19 9l-7 7-7-7"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth="2"
                                                                />
                                                            </svg>
                                                        ) : (
                                                            <svg
                                                                className="h-4 w-4 inline text-zinc-400"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth="2"
                                                                />
                                                            </svg>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                        {table.getRowModel().rows.map((row) => (
                            <Fragment key={`row-${row.id}`}>
                                <tr
                                    className={`hover:bg-[#333333] ${onRowClick ? 'cursor-pointer' : ''}`}
                                    onClick={() => onRowClick?.(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <td
                                            className="px-3 py-3 break-words overflow-hidden text-zinc-50"
                                            key={`cell-${cell.id}`}
                                            style={{
                                                minWidth: cell.column.columnDef.minSize ? `${cell.column.columnDef.minSize}px` : undefined,
                                                maxWidth: cell.column.columnDef.maxSize ? `${cell.column.columnDef.maxSize}px` : undefined,
                                                width: cell.column.columnDef.size ? `${cell.column.columnDef.size}px` : undefined
                                            }}
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </td>
                                    ))}
                                </tr>
                                {row.getIsExpanded() && renderSubComponent ? (
                                    <tr>
                                        <td className="p-0" colSpan={row.getVisibleCells().length}>
                                            <div className="bg-zinc-900/50 border-l-4 border-zinc-700">{renderSubComponent({ row })}</div>
                                        </td>
                                    </tr>
                                ) : null}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {pagination.enabled && table.getPageCount() > 1 && (
                <div className="flex items-center justify-center mt-4 gap-3">
                    <button
                        className={`flex items-center justify-center w-8 h-8 rounded border border-zinc-800 ${
                            !table.getCanPreviousPage()
                                ? 'bg-[#222222] text-zinc-500 cursor-not-allowed opacity-50'
                                : 'bg-[#222222] text-zinc-300 hover:bg-[#333333]'
                        }`}
                        disabled={!table.getCanPreviousPage()}
                        onClick={() => table.previousPage()}
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                        </svg>
                    </button>
                    <span className="text-sm text-zinc-300">
                        Page {table.state.pagination.pageIndex + 1} of {table.getPageCount()}
                    </span>
                    <button
                        className={`flex items-center justify-center w-8 h-8 rounded border border-zinc-800 ${
                            !table.getCanNextPage()
                                ? 'bg-[#222222] text-zinc-500 cursor-not-allowed opacity-50'
                                : 'bg-[#222222] text-zinc-300 hover:bg-[#333333]'
                        }`}
                        disabled={!table.getCanNextPage()}
                        onClick={() => table.nextPage()}
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
}

export default ExpandableDataTable;
